import { useState, useCallback, useMemo, useEffect } from "react";
import type { ReportDataResponse } from "../types";

/**
 * Generic hook for fetching and managing report data
 * Handles loading, error states, and provides refetch capability
 * 
 * @param defaultData - Initial/default data state
 * @param fetchFn - Async function that fetches and transforms data
 * @returns Report data with loading, error, and refetch
 * 
 * @example
 * const useMyReport = () => {
 *   const fetchAndTransform = useCallback(async () => {
 *     const raw = await fetchMyData();
 *     return transformData(raw);
 *   }, []);
 *   
 *   return useReportData(DEFAULT_DATA, fetchAndTransform);
 * };
 */
export const useReportData = <T>(
  defaultData: T,
  fetchFn: () => Promise<T>,
): ReportDataResponse<T> => {
  const [data, setData] = useState<T>(defaultData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchFn()
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to fetch report data");
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [fetchFn]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchFn();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch report data");
    } finally {
      setLoading(false);
    }
  }, [fetchFn]);

  return useMemo(
    () => ({
      data,
      loading,
      error,
      refetch: fetchData,
    }),
    [data, loading, error, fetchData]
  );
};
